import { useCallback } from "react";
import { useAuth } from "./useAuth";

/**
 * Task API 연동 훅
 * - 일정 CRUD, 상태 변경, 조회
 * - status: true/false 기반 성공여부
 * - view_only 플래그 처리
 */
export default function useTask() {
  const { api } = useAuth();

  const toDateOnly = (d) => {
    if (!d) return null;
    if (typeof d === "string") return d.length > 10 ? d.slice(0, 10) : d;
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    return String(d).slice(0, 10);
  };
  /** 일정 생성 */
  const createTask = useCallback(
    async ({
      calendar_id,
      title,
      description,
      start_time,
      end_time,
      duration,
      is_all_day,
      category,
      priority,
      location,
      reminder,
      repeat_config,
      task_date,
      attendee_ids,
      attendee_files,
    }) => {
      try {
        // 시간 형식 변환 (HH:MM -> HH:MM:SS)
        const formatTime = (time) => {
          if (!time) return null;
          return time.includes(":") && time.split(":").length === 2
            ? `${time}`
            : time;
        };
        const { data } = await api.post("/calendar/addtask", {
          calendar_id,
          title,
          description: description || "",
          start_time: formatTime(start_time),
          end_time: formatTime(end_time),
          duration: duration || null,
          is_all_day: is_all_day || false,
          category: category || "work",
          priority: priority || "medium",
          location: location || "",
          reminder: reminder || 0,
          repeat_config,
          task_date,
          attendee_ids: attendee_ids || [],
          attendee_files: attendee_files || [],
        });
        return {
          ok: !!data?.status,
          viewOnly: !!data?.view_only,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 일정 수정 */
  const updateTask = useCallback(
    async ({
      calendar_id,
      id,
      title,
      description,
      start_time,
      end_time,
      location,
      category,
      priority,
      is_event,
      add_attendee_ids,
      remove_attendee_ids,
      attendee_files,
    }) => {
      try {
        // 시간 형식 변환 (HH:MM -> HH:MM:SS)
        const formatTime = (time) => {
          if (!time) return null;
          return time.includes(":") && time.split(":").length === 2
            ? `${time}:00`
            : time;
        };
        const { data } = await api.post("/calendar/updatetask", {
          calendar_id,
          id,
          title,
          description,
          start_time: formatTime(start_time),
          end_time: formatTime(end_time),
          location,
          category,
          priority,
          is_event,
          add_attendee_ids,
          remove_attendee_ids,
          attendee_files,
        });
        return {
          ok: !!data?.status,
          viewOnly: !!data?.view_only,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 일정 삭제 */
  const deleteTask = useCallback(
    async ({ calendar_id, id, is_recurring, date }) => {
      try {
        const { data } = await api.post("/calendar/deletetask", {
          calendar_id,
          id,
          is_recurring,
          date,
        });
        return {
          ok: !!data?.status,
          viewOnly: !!data?.view_only,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 월별 일정 조회 */
  const fetchMonthlyTasks = useCallback(
    async (calendars, year, month) => {
      // 캘린더가 선택되지 않았으면 빈 결과 반환
      if (!calendars || calendars.length === 0) {
        return {
          ok: true,
          data: {
            year,
            month,
            count: 0,
            by_calendar: {},
          },
        };
      }

      try {
        const { data } = await api.post("/calendar/viewtask", {
          calendars,
          year,
          month,
        });

        if (!data?.status) {
          return { ok: false, data: {} };
        }
        const result = data.data || {};
        console.log(result);
        return {
          ok: true,
          data: {
            year: result.year,
            month: result.month,
            count: result.count,
            by_calendar: result.by_calendar || {},
          },
        };
      } catch (e) {
        return { ok: false, data: {} };
      }
    },
    [api]
  );

  /** 일정 상세 조회 */
  const fetchTaskDetail = useCallback(
    async ({ task_id, is_virtual_event, recurring_event_id, date }) => {
      try {
        const { data } = await api.post("/calendar/viewtask/detail", {
          task_id,
          is_virtual_event,
          recurring_event_id,
          date,
        });
        if (!data?.status || !data?.data) {
          return { ok: false, detail: null };
        }
        return {
          ok: true,
          detail: data.data,
        };
      } catch (e) {
        return { ok: false, detail: null };
      }
    },
    [api]
  );

  /** 일정 상태 변경 */
  const changeTaskStatus = useCallback(
    async ({ calendar_id, task_id, status }) => {
      try {
        const { data } = await api.post("/calendar/task/status/changed", {
          calendar_id,
          task_id,
          status,
        });
        return {
          ok: !!data?.status,
          viewOnly: !!data?.view_only,
        };
      } catch (e) {
        return { ok: false };
      }
    },
    [api]
  );

  /** 오늘 할 일 목록 조회 */
  const fetchTodayTodo = useCallback(async () => {
    try {
      const { data } = await api.post("/calendar/task/todo");
      if (!data?.status) {
        return { ok: false, todos: [] };
      }
      const raw = Array.isArray(data.data) ? data.data : [];
      const todos = raw.map((t) => ({
        id: t.id,
        calendar_id: t.calendar_id,
        creator_id: t.creator_id,
        title: t.title,
        description: t.description,
        task_date: t.task_date,
        start_time: t.start_time,
        end_time: t.end_time,
        duration: t.duration,
        is_all_day: t.is_all_day,
        category: t.category,
        priority: t.priority,
        status: t.status,
        location: t.location,
        reminder: t.reminder,
        recurring_event_id: t.recurring_event_id,
        completed_at: t.completed_at,
        created_at: t.created_at,
        updated_at: t.updated_at,
      }));
      return { ok: true, todos };
    } catch (e) {
      return { ok: false, todos: [] };
    }
  }, [api]);

  return {
    createTask,
    updateTask,
    deleteTask,
    fetchMonthlyTasks,
    fetchTaskDetail,
    changeTaskStatus,
    fetchTodayTodo,
  };
}
